import React, { useState, useEffect } from 'react';
import { extinguisherService, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import UserManagement from './UserManagement';
import ConfirmationModal from './ConfirmationModal';
import ImageModal from './ImageModal';
import { FileDownload } from '@mui/icons-material';

function AdminDashboard() {
  const { logout } = useAuth();
  const [extinguishers, setExtinguishers] = useState([]);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('extinguishers');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showFullImage, setShowFullImage] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Force scrollbar to always be visible to prevent layout bounce when switching tabs
    document.documentElement.style.overflowY = 'scroll';
    // Disable smooth scrolling to prevent jumping
    document.documentElement.style.scrollBehavior = 'auto';
    
    return () => {
      // Cleanup: restore default overflow behavior when component unmounts
      document.documentElement.style.overflowY = '';
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.data?.error === 'Unauthorized') {
      console.log('Authentication error detected, logging out user');
      logout();
      setError('Session expired. Please log in again.');
      return true;
    }
    return false;
  };

  const loadExtinguishers = async () => {
    try {
      const response = await extinguisherService.getAll();
      setExtinguishers(response.data);
      setError(''); // Clear any previous errors
    } catch (error) {
      if (!handleAuthError(error)) {
        setError('Failed to load extinguishers');
        console.error('Load error:', error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadExtinguishers();
  }, []);

  const loadWeeklyReports = async () => {
    setReportsLoading(true);
    try {
      console.log('Loading weekly reports...');
      const response = await userService.getWeeklyReports();
      console.log('Weekly reports response:', response);
      setWeeklyReports(response.data);
      setError(''); // Clear any previous errors
    } catch (error) {
      if (!handleAuthError(error)) {
        setError('Failed to load weekly reports');
        console.error('Weekly reports error:', error);
        console.error('Error details:', error.response);
      }
    }
    setReportsLoading(false);
  };

  const handleDelete = async (id) => {
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      await extinguisherService.delete(showDeleteConfirm);
      setExtinguishers(extinguishers.filter(ext => ext.id !== showDeleteConfirm));
      setSuccessMessage('Extinguisher deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000); // Clear after 3 seconds
    } catch (error) {
      if (!handleAuthError(error)) {
        setError('Failed to delete extinguisher');
        console.error('Delete error:', error);
      }
    }
    setShowDeleteConfirm(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const isExpiringSoon = (expirationDate) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    const daysUntilExpiration = Math.ceil((expiration - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 30;
  };

  const isExpired = (expirationDate) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    return expiration < today;
  };

  const getStatus = (expirationDate) => {
    if (isExpired(expirationDate)) return 'Expired';
    if (isExpiringSoon(expirationDate)) return 'Expiring Soon';
    return 'Valid';
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Address', 'Latitude', 'Longitude', 'Expiration Date', 'Service Provider', 'Location Type', 'Estimated Units', 'Status', 'Created Date', 'User ID'];
    
    const csvData = extinguishers.map(ext => [
      ext.id,
      `"${ext.address}"`, // Quote address in case it contains commas
      ext.lat,
      ext.lng,
      new Date(ext.expiration).toLocaleDateString(),
      `"${ext.service_provider || 'N/A'}"`, // Quote service provider in case it contains commas
      `"${ext.location_type || 'N/A'}"`, // Quote location type in case it contains commas
      ext.estimated_units || 'N/A', // Estimated units
      getStatus(ext.expiration),
      new Date(ext.created_at).toLocaleDateString(),
      ext.user_id || 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const today = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `fire-extinguishers-${today}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabSwitch = (newTab) => {
    setActiveTab(newTab);
    
    // For reports tab, load data if needed
    if (newTab === 'reports' && weeklyReports.length === 0 && !reportsLoading) {
      loadWeeklyReports();
    }
  };

  const tabButtonStyle = (tab) => ({
    padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
    border: 'none',
    backgroundColor: activeTab === tab ? '#6c757d' : 'transparent',
    color: activeTab === tab ? 'white' : '#6c757d',
    borderBottom: activeTab === tab ? '2px solid #6c757d' : '2px solid transparent',
    cursor: 'pointer',
    fontSize: isMobile ? '0.875rem' : '1rem',
    whiteSpace: 'nowrap',
    minWidth: 'fit-content'
  });

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>
      {error && <div className="error-message">{error}</div>}
      
      {/* Toast-style success message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          backgroundColor: '#28a745',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontWeight: 'bold',
          animation: 'fadeInOut 3s ease'
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem', borderBottom: '1px solid #dee2e6' }}>
        <div style={{ 
          display: 'flex', 
          gap: '0px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <button 
            onClick={(e) => {
              e.preventDefault();
              handleTabSwitch('extinguishers');
            }}
            style={tabButtonStyle('extinguishers')}
          >
            {isMobile ? 'Extinguishers' : 'Fire Extinguishers'}
          </button>
          <button 
            onClick={(e) => {
              e.preventDefault();
              handleTabSwitch('users');
            }}
            style={tabButtonStyle('users')}
          >
            {isMobile ? 'Users' : 'User Management'}
          </button>
          <button 
            onClick={(e) => {
              e.preventDefault();
              handleTabSwitch('reports');
            }}
            style={tabButtonStyle('reports')}
          >
            {isMobile ? 'Reports' : 'Weekly Reports'}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ display: activeTab === 'extinguishers' ? 'block' : 'none' }}>
        <div>
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Statistics</h3>
              <button 
                onClick={exportToCSV}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                disabled={extinguishers.length === 0}
              >
                <FileDownload sx={{ fontSize: '1.1rem' }} /> Export to CSV
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ background: '#e8f5e8', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.length}</strong><br />
                Total Extinguishers
              </div>
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.filter(ext => isExpiringSoon(ext.expiration) && !isExpired(ext.expiration)).length}</strong><br />
                Expiring Soon (30 days)
              </div>
              <div style={{ background: '#f8d7da', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.filter(ext => isExpired(ext.expiration)).length}</strong><br />
                Expired
              </div>
            </div>
          </div>

          <h3>All Fire Extinguishers</h3>
          
          {extinguishers.length === 0 ? (
            <p>No fire extinguishers reported yet.</p>
          ) : (
            <div className="extinguisher-list">
              {extinguishers.map((extinguisher) => (
                <div 
                  key={extinguisher.id} 
                  className="extinguisher-card"
                  style={{
                    borderLeft: isExpired(extinguisher.expiration) 
                      ? '4px solid #dc3545' 
                      : isExpiringSoon(extinguisher.expiration) 
                        ? '4px solid #ffc107' 
                        : '4px solid #28a745'
                  }}
                >
                  <div className="extinguisher-details">
                    <div className="extinguisher-info">
                      <h3>{extinguisher.address}</h3>
                      <p><strong>Expiration:</strong> {new Date(extinguisher.expiration).toLocaleDateString()}</p>
                      <p><strong>Service Provider:</strong> {extinguisher.service_provider || 'N/A'}</p>
                      <p><strong>Coordinates:</strong> {
                        extinguisher.lat !== null && extinguisher.lng !== null 
                          ? `${extinguisher.lat.toFixed(4)}, ${extinguisher.lng.toFixed(4)}`
                          : 'Not available'
                      }</p>
                      <p><strong>Reported:</strong> {new Date(extinguisher.created_at).toLocaleDateString()}</p>
                      {extinguisher.user_id && <p><strong>User ID:</strong> {extinguisher.user_id}</p>}
                      
                      <div style={{ marginTop: '1rem' }}>
                        <button 
                          className="btn" 
                          style={{ backgroundColor: '#dc3545', marginRight: '0.5rem' }}
                          onClick={() => handleDelete(extinguisher.id)}
                        >
                          Delete
                        </button>
                        {isExpired(extinguisher.expiration) && (
                          <span style={{ color: '#dc3545', fontWeight: 'bold' }}>EXPIRED</span>
                        )}
                        {isExpiringSoon(extinguisher.expiration) && !isExpired(extinguisher.expiration) && (
                          <span style={{ color: '#ffc107', fontWeight: 'bold' }}>EXPIRING SOON</span>
                        )}
                      </div>
                    </div>
                    
                    {extinguisher.image_url && (
                      <div>
                        <img 
                          src={extinguisher.image_url} 
                          alt="Fire Extinguisher" 
                          className="extinguisher-image"
                          onClick={() => setShowFullImage(extinguisher.image_url)}
                          style={{
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                          title="Click to view full size"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: activeTab === 'users' ? 'block' : 'none' }}>
        <UserManagement />
      </div>

      <div style={{ display: activeTab === 'reports' ? 'block' : 'none' }}>
        <div>
          <div style={{ marginBottom: '2rem' }}>
            <h3>Weekly Fire Extinguisher Reports</h3>
            <p style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
              Number of fire extinguishers reported by each user in the last 7 days
            </p>
            
            {reportsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                Loading weekly reports...
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div style={{ overflowX: 'auto', display: isMobile ? 'none' : 'block' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                          User
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                          Reports This Week
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyReports.map((user, index) => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '1rem', fontWeight: '500' }}>
                            {user.username}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.5rem 1rem',
                              borderRadius: '20px',
                              fontSize: '1.1rem',
                              fontWeight: 'bold',
                              backgroundColor: user.extinguishers_this_week > 0 ? '#28a745' : '#f8f9fa',
                              color: user.extinguishers_this_week > 0 ? 'white' : '#6c757d'
                            }}>
                              {user.extinguishers_this_week}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            {user.extinguishers_this_week > 0 ? (
                              <span style={{ color: '#28a745', fontWeight: '500' }}>✓ Active</span>
                            ) : (
                              <span style={{ color: '#6c757d' }}>— Inactive</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div style={{ display: isMobile ? 'block' : 'none' }}>
                  {weeklyReports.map((user, index) => (
                    <div key={user.id} style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                          {user.username}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Reports:</span>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '15px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            backgroundColor: user.extinguishers_this_week > 0 ? '#28a745' : '#f8f9fa',
                            color: user.extinguishers_this_week > 0 ? 'white' : '#6c757d'
                          }}>
                            {user.extinguishers_this_week}
                          </span>
                        </div>
                        
                        <div>
                          {user.extinguishers_this_week > 0 ? (
                            <span style={{ color: '#28a745', fontWeight: '500', fontSize: '0.9rem' }}>✓ Active</span>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>— Inactive</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {weeklyReports.length === 0 && !reportsLoading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                No user data available
              </div>
            )}

            {/* Summary Statistics */}
            {weeklyReports.length > 0 && (
              <div style={{ 
                marginTop: '2rem', 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem' 
              }}>
                <div style={{ 
                  backgroundColor: '#e3f2fd', 
                  padding: isMobile ? '1rem' : '1.5rem', 
                  borderRadius: '8px', 
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    fontSize: isMobile ? '1.5rem' : '2rem', 
                    fontWeight: 'bold', 
                    color: '#1976d2' 
                  }}>
                    {weeklyReports.filter(user => user.extinguishers_this_week > 0).length}
                  </div>
                  <div style={{ 
                    color: '#1976d2', 
                    fontWeight: '500',
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>Active Users</div>
                </div>
                <div style={{ 
                  backgroundColor: '#f3e5f5', 
                  padding: isMobile ? '1rem' : '1.5rem', 
                  borderRadius: '8px', 
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    fontSize: isMobile ? '1.5rem' : '2rem', 
                    fontWeight: 'bold', 
                    color: '#7b1fa2' 
                  }}>
                    {weeklyReports.reduce((sum, user) => sum + user.extinguishers_this_week, 0)}
                  </div>
                  <div style={{ 
                    color: '#7b1fa2', 
                    fontWeight: '500',
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>Total Reports</div>
                </div>
                <div style={{ 
                  backgroundColor: '#e8f5e8', 
                  padding: isMobile ? '1rem' : '1.5rem', 
                  borderRadius: '8px', 
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    fontSize: isMobile ? '1.5rem' : '2rem', 
                    fontWeight: 'bold', 
                    color: '#388e3c' 
                  }}>
                    {Math.round((weeklyReports.reduce((sum, user) => sum + user.extinguishers_this_week, 0) / weeklyReports.length) * 10) / 10}
                  </div>
                  <div style={{ 
                    color: '#388e3c', 
                    fontWeight: '500',
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>Avg per User</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      <ImageModal 
        isVisible={!!showFullImage}
        imageUrl={showFullImage}
        altText="Full size fire extinguisher"
        onClose={() => setShowFullImage(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isVisible={!!showDeleteConfirm}
        title="FireWatch"
        message="Delete extinguisher?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isDestructive={true}
      />
    </div>
  );
}

export default AdminDashboard;